import { SaveButton, Show } from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import {
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Statistic,
} from 'antd';
import BatchSection from 'components/product-make/BatchSection';
import ExpenseSection from 'components/product-make/ExpenseSection';
import RecipeTable from 'components/product-make/RecipeTable';
import WastageSection from 'components/product-make/WastageSection';
import { ManufactureStatus } from 'enums';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  findRecipeWithStatus,
  getManufacture,
  updateProductManufacture,
} from 'services/product-manufacture';
import { calculateTotalManufacturingCost, scrollToFieldWithOffset } from 'utilities/shared';

interface RecipeDetailsResponse {
  madeUpVariantName?: string;
  madeUpVariantSku?: string;
  purchasePrice?: number;
  sellingPrice?: number;
  usedProductRecipes?: UsedProductRecipe[];
  madeUpVariantId?: string;
}

interface UsedProductRecipe {
  inventoryQuantity?: number;
  availability?: string;
  note?: string;
  usedProductCost?: number;
  usedProductQuantity?: number;
  usedVariantId?: string;
  usedVariantName?: string;
  usedVariantSku?: string;
}

const { Option } = Select;

// Allowed status transitions
const allowedTransitions: Record<ManufactureStatus, ManufactureStatus[]> = {
  [ManufactureStatus['Not Started']]: [
    ManufactureStatus['Not Started'],
    ManufactureStatus['In Progress'],
    ManufactureStatus['Done'],
  ],
  [ManufactureStatus['In Progress']]: [ManufactureStatus['In Progress'], ManufactureStatus['Done']],
  [ManufactureStatus['Done']]: [ManufactureStatus['Done']],
};

export const MakeProductEdit: React.FC = () => {
  const { id } = useParams();
  const { list } = useNavigation();
  const [form] = Form.useForm();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [recipeDetails, setRecipeDetails] = useState<RecipeDetailsResponse | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const [currentStatus, setCurrentStatus] = useState<ManufactureStatus | null>(null);
  // Batch Section State
  const [batchData, setBatchData] = useState<any[]>([]);
  const [editingBatch, setEditingBatch] = useState<any>(null);
  const [batchDrawerVisible, setBatchDrawerVisible] = useState<boolean>(false);
  // Wastage Section State
  const [wastageData, setWastageData] = useState<any[]>([]);
  const [editingWastage, setEditingWastage] = useState<any>(null);
  const [wastageDrawerVisible, setWastageDrawerVisible] = useState<boolean>(false);
  // Expense Section State
  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [editingExpense, setEditingExpense] = useState<any>(null);
  const [expenseDrawerVisible, setExpenseDrawerVisible] = useState<boolean>(false);

  const isStatusTransitionValid = (newStatus: ManufactureStatus): boolean => {
    if (!currentStatus) return true; // Allow if no current status (initial load)
    return allowedTransitions[currentStatus].includes(newStatus);
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          setIsFetching(true);
          const manufactureDetails = await getManufacture(id);
          setCurrentStatus(manufactureDetails.manufactureStatus as ManufactureStatus);

          const recipeInput = {
            data: {
              variantId: manufactureDetails.productVariant.id,
              variantQuantity: Number(manufactureDetails.quantity),
              warehouseId: manufactureDetails.warehouse.id,
            },
          };
          const recipeDetailsResponse = await findRecipeWithStatus(recipeInput);
          setRecipeDetails(recipeDetailsResponse);

          // Handle case where batchInfo might be null
          setBatchData(
            manufactureDetails?.batchInfo
              ? [
                {
                  key: manufactureDetails?.id,
                  batch: manufactureDetails.batchInfo.batchNo || '',
                  expiration: manufactureDetails.batchInfo.expirationDate
                    ? new Date(manufactureDetails.batchInfo.expirationDate).toLocaleDateString()
                    : '',
                  notes: manufactureDetails.batchInfo.notes || '',
                },
              ]
              : []
          );

          // Transform `manufactureExpense` into `expenseData` format
          if (manufactureDetails?.manufactureExpense?.length > 0) {
            setExpenseData(
              manufactureDetails?.manufactureExpense.map(
                (
                  expense: { name: any; notes: any; amount: { toString: () => any } },
                  index: any
                ) => ({
                  key: `${manufactureDetails?.id}-exp-${index}`, // Unique key
                  expenseCategory: expense.name || 'N/A',
                  note: expense.notes || '',
                  amount: expense.amount?.toString() || '0',
                })
              )
            );
          } else {
            setExpenseData([]); // Ensure empty state if no expenses
          }

          // Transform `manufactureWastage` into `wastageData` format
          if (manufactureDetails?.manufactureWastage?.length > 0) {
            setWastageData(
              manufactureDetails?.manufactureWastage.map(
                (
                  wastage: {
                    material: any;
                    quantity: { toString: () => any };
                    notes: any;
                    wastageCost: { toString: () => any };
                  },
                  index: any
                ) => ({
                  key: `${manufactureDetails?.id}-wst-${index}`, // Unique key
                  material: wastage.material || 'N/A',
                  quantity: wastage.quantity?.toString() || '0',
                  notes: wastage.notes || '',
                  cost: wastage.wastageCost?.toString() || '0',
                })
              )
            );
          } else {
            setWastageData([]); // Ensure empty state if no wastage
          }

          form.setFieldsValue({
            warehouse: manufactureDetails?.warehouse?.id || undefined,
            product: manufactureDetails?.productVariant?.name || undefined,
            quantity: manufactureDetails?.quantity || null,
            productStatus: manufactureDetails?.manufactureStatus || undefined,
            deadline: manufactureDetails?.productionDeadlineDate
              ? moment(manufactureDetails.productionDeadlineDate)
              : null,
          });
        } catch (error: any) {
          notification.error({
            message: 'Error',
            description: error.message,
          });
        } finally {
          setIsFetching(false);
        }
      }
    })();
  }, [form, id]);

  const onStatusCheck = (value: string) => {
    const newStatus = value as ManufactureStatus;

    // Check if this is a valid transition
    if (!isStatusTransitionValid(newStatus)) {
      setIsSaveDisabled(true);
      notification.warning({
        message: 'Invalid Status Transition',
        description: `Cannot change status from ${currentStatus} to ${newStatus}. Allowed transitions are: ${allowedTransitions[currentStatus as ManufactureStatus]?.join(', ') || 'none'
          }`,
      });
      return;
    }

    if (recipeDetails && recipeDetails.usedProductRecipes) {
      const unavailableMaterials = recipeDetails.usedProductRecipes.filter(
        (recipe) => recipe.availability && recipe.availability === 'Not Available'
      );

      const hasUnavailableMaterials = unavailableMaterials.length > 0;
      const shouldBlockStatusChange =
        hasUnavailableMaterials &&
        newStatus === ManufactureStatus['In Progress'] &&
        currentStatus !== ManufactureStatus['In Progress'];

      if (shouldBlockStatusChange) {
        setIsSaveDisabled(true);
        notification.warning({
          message: 'Material Unavailable',
          description:
            'Some materials are not available. Please reduce the quantity or check the availability of materials.',
        });
        return;
      }

      setIsSaveDisabled(false);
    }
  };

  // Handlers for Batch Section
  const handleBatchEdit = (record: any) => {
    setEditingBatch(record);
    setBatchDrawerVisible(true);
  };

  const handleBatchDelete = (key: string) => {
    setBatchData(batchData.filter((item) => item.key !== key));
  };

  const handleBatchSubmit = (newBatch: any) => {
    if (editingBatch) {
      setBatchData(
        batchData.map((item) => (item.key === editingBatch.key ? { ...item, ...newBatch } : item))
      );
    } else {
      setBatchData([...batchData, { ...newBatch, key: Date.now().toString() }]);
    }
    setEditingBatch(null);
    setBatchDrawerVisible(false);
  };

  // Handlers for Wastage Section
  const handleWastageEdit = (record: any) => {
    setEditingWastage(record);
    setWastageDrawerVisible(true);
  };

  const handleWastageDelete = (key: string) => {
    setWastageData(wastageData.filter((item) => item.key !== key));
  };

  const handleWastageSubmit = (newWastage: any) => {
    if (editingWastage) {
      setWastageData(
        wastageData.map((item) =>
          item.key === editingWastage.key ? { ...item, ...newWastage } : item
        )
      );
    } else {
      setWastageData([...wastageData, { ...newWastage, key: Date.now().toString() }]);
    }
    setEditingWastage(null);
    setWastageDrawerVisible(false);
  };

  // Handlers for Expense Section
  const handleExpenseEdit = (record: any) => {
    setEditingExpense(record);
    setExpenseDrawerVisible(true);
  };

  const handleExpenseDelete = (key: string) => {
    setExpenseData(expenseData.filter((item) => item.key !== key));
  };

  const handleExpenseSubmit = (newExpense: any) => {
    if (editingExpense) {
      setExpenseData(
        expenseData.map((item) =>
          item.key === editingExpense.key ? { ...item, ...newExpense } : item
        )
      );
    } else {
      setExpenseData([...expenseData, { ...newExpense, key: Date.now().toString() }]);
    }
    setEditingExpense(null);
    setExpenseDrawerVisible(false);
  };

  // General Form Validation Function
  const getFormValidationErrors = (formValues: any) => {
    const errors: any[] = [];

    // Deadline validation
    if (!formValues.deadline) {
      errors.push({
        name: 'deadline',
        errors: ['Please select a deadline.'],
      });
    } else {
      const today = new Date();
      const selectedDate = new Date(formValues.deadline);
      if (selectedDate.getTime() < today.setHours(0, 0, 0, 0)) {
        errors.push({
          name: 'deadline',
          errors: ['Deadline cannot be in the past.'],
        });
      }
    }

    if (!formValues.productStatus) {
      errors.push({
        name: 'productStatus',
        errors: ['Please select a product status.'],
      });
    }

    return errors.length ? errors : null;
  };

  const handleSave = async () => {
    setIsFetching(true);
    try {
      // Get form data
      const formData = await form.getFieldsValue();
      const validationError = getFormValidationErrors(formData);

      if (validationError !== null) {
        form.setFields(validationError);
        scrollToFieldWithOffset(validationError[0].name as string, 100);
        return;
      }

      const newStatus = formData.productStatus as ManufactureStatus;

      // Double-check status transition (in case user bypassed UI validation)
      if (!isStatusTransitionValid(newStatus)) {
        notification.warning({
          message: 'Invalid Status Transition',
          description: `Cannot change status from ${currentStatus} to ${newStatus}. Allowed transitions are: ${allowedTransitions[currentStatus as ManufactureStatus]?.join(', ') || 'none'
            }`,
        });
        return;
      }

      const manufactureExpense = expenseData.map((item) => ({
        name: item.expenseCategory,
        notes: item.note || '',
        amount: Number(item.amount),
      }));

      const manufactureWastage = wastageData.map((item) => ({
        material: item.material,
        notes: item.notes || '',
        quantity: Number(item.quantity),
        wastageCost: Number(item.cost),
      }));

      let productionDeadlineDate = new Date();
      if (formData.deadline && !isNaN(new Date(formData.deadline).getTime())) {
        productionDeadlineDate = new Date(formData.deadline);
      }
      const formattedDeadlineDate = productionDeadlineDate.toISOString();

      let expirationDate: Date | undefined;
      if (batchData.length > 0 && batchData[0].expiration) {
        const [day, month, year] = batchData[0].expiration.split('-');
        const date = new Date(`${year}-${month}-${day}`);
        if (!isNaN(date.getTime())) {
          expirationDate = date;
        }
      }
      const formattedExpirationDate = expirationDate?.toISOString() ?? null;

      const batchInfo =
        batchData.length > 0
          ? {
            batchNo: batchData[0]?.batch || 'N/A',
            expirationDate: formattedExpirationDate,
            notes: batchData[0]?.notes || '',
          }
          : null;

      const requestData = {
        productionDeadlineDate: formattedDeadlineDate,
        manufactureStatus: formData.productStatus,
        manufactureExpense,
        manufactureWastage,
        batchInfo,
      };

      if (id) {
        await updateProductManufacture(id, requestData);
        setCurrentStatus(newStatus); // Update current status after successful save
      } else {
        notification.error({
          message: 'Error',
          description: 'Manufacture Id is undefined',
        });
      }

      notification.success({
        message: 'Success',
        description: 'Updated successfully!',
      });

      form.resetFields();
      list('product-make');
    } catch (error: any) {
      console.error('Submission Failed:', error);
      notification.error({
        message: 'Error',
        description: error?.message || 'An unknown error occurred',
      });
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Show headerProps={{ extra: null }} isLoading={isFetching}>
      {/* General Information Form */}
      <Form form={form} layout="vertical" style={{ marginBottom: 32 }}>
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={12}>
            <Card title="Edit Manufacture">
              <Form.Item
                label="Warehouse"
                name="warehouse"
                rules={[{ required: true, message: 'Please select a warehouse' }]}
              >
                <Input type="text" placeholder="Warehouse Name" disabled={true} />
              </Form.Item>

              <Form.Item
                label="Product"
                name="product"
                rules={[{ required: true, message: 'Please select a product' }]}
              >
                <Input type="text" placeholder="Product Name" disabled={true} />
              </Form.Item>

              <Form.Item label="Planned Quantity" name="quantity">
                <Input type="number" placeholder="Enter Quantity" disabled={true} />
              </Form.Item>

              <Form.Item label="Product Status" name="productStatus">
                <Select onSelect={(value) => onStatusCheck(value)}>
                  {Object.values(ManufactureStatus).map((status) => (
                    <Option
                      key={status}
                      value={status}
                      disabled={!isStatusTransitionValid(status as ManufactureStatus)}
                    >
                      {status}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Production Deadline" name="deadline">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
      {/* Recipe Table */}
      <RecipeTable
        recipeData={recipeDetails}
        loading={isFetching}
        expenseData={expenseData}
        wastageData={wastageData}
      />
      <Divider />
      {/* Batch Section */}
      <BatchSection
        data={batchData}
        visible={batchDrawerVisible}
        editingRecord={editingBatch}
        onEdit={handleBatchEdit}
        onDelete={handleBatchDelete}
        onSubmit={handleBatchSubmit}
        onClose={() => setBatchDrawerVisible(false)}
        disabled={isSaveDisabled}
      />
      <Divider />
      {/* Wastage Section */}
      <WastageSection
        data={wastageData}
        visible={wastageDrawerVisible}
        editingRecord={editingWastage}
        recipeDetails={recipeDetails} // Pass recipeDetails here
        onEdit={handleWastageEdit}
        onDelete={handleWastageDelete}
        onSubmit={handleWastageSubmit}
        onClose={() => setWastageDrawerVisible(false)}
        disabled={isSaveDisabled}
      />
      <Divider />
      {/* Expense Section */}
      <ExpenseSection
        data={expenseData}
        visible={expenseDrawerVisible}
        editingRecord={editingExpense}
        onEdit={handleExpenseEdit}
        onDelete={handleExpenseDelete}
        onSubmit={handleExpenseSubmit}
        onClose={() => setExpenseDrawerVisible(false)}
        disabled={isSaveDisabled}
      />
      <Divider />

      {/* Total Manufacturing Cost Calculation */}
      <Card style={{ marginBottom: 16 }} hidden>
        <Row justify="end">
          <Col>
            <Statistic
              title="Total Manufacturing Cost"
              value={`${calculateTotalManufacturingCost(
                recipeDetails?.usedProductRecipes,
                wastageData,
                expenseData
              ).toFixed(2)}`}
              valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Save Button */}
      <Space style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
        <SaveButton
          type="primary"
          style={{ marginTop: 16 }}
          onClick={handleSave}
          disabled={isSaveDisabled}
          loading={isFetching}
        >
          Save
        </SaveButton>
      </Space>
    </Show>
  );
};
